import { useState, useEffect } from "react";
import { Coins, Plus, Trash2, Loader2, Download } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { exportCsv } from "../../utils/primaNotaHelpers";

export default function PrimaNotaListinoTab({
  voci,
  onChange,
  onSave,
  saving,
}) {
  const [draft, setDraft] = useState(voci || []);

  useEffect(() => {
    setDraft(voci || []);
  }, [voci]);

  const syncDraft = (next) => {
    setDraft(next);
    onChange?.(next);
  };

  const ausili = draft.filter((v) => v.categoria === "ausili");
  const servizi = draft.filter((v) => v.categoria === "servizi");

  const addVoce = (categoria) => {
    syncDraft([
      ...draft,
      {
        id: `tmp-${Date.now()}`,
        categoria,
        codice: "",
        nome: "",
        descrizione: "",
        prezzoUnitario: "",
        pezziPerPacco: categoria === "ausili" ? 30 : null,
        attivo: true,
      },
    ]);
  };

  const renderTable = (rows, categoria, title) => (
    <div className="mb-6">
      <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-2">{title}</h3>
      <div className="overflow-x-auto border rounded-xl">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Voce di listino</TableHead>
              <TableHead>Descrizione</TableHead>
              <TableHead className="text-right">Prezzo unitario (€)</TableHead>
              <TableHead className="w-16" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-slate-500 py-6">
                  Nessuna voce
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const idx = draft.findIndex((v) => v.id === row.id);
                return (
                  <TableRow key={row.id || idx}>
                    <TableCell>
                      <Input
                        value={row.nome || ""}
                        onChange={(e) => {
                          const n = [...draft];
                          n[idx] = { ...n[idx], nome: e.target.value, codice: (n[idx].codice || e.target.value).toLowerCase().replace(/\s+/g, "_") };
                          syncDraft(n);
                        }}
                        placeholder="Nome voce"
                        className="max-w-[180px]"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={row.descrizione || ""}
                        onChange={(e) => {
                          const n = [...draft];
                          n[idx] = { ...n[idx], descrizione: e.target.value };
                          syncDraft(n);
                        }}
                        placeholder="Descrizione"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="text"
                        inputMode="decimal"
                        className="max-w-[100px] ml-auto"
                        value={row.prezzoUnitario ?? ""}
                        onChange={(e) => {
                          const n = [...draft];
                          n[idx] = { ...n[idx], prezzoUnitario: e.target.value };
                          syncDraft(n);
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-600"
                        onClick={() => syncDraft(draft.filter((_, i) => i !== idx))}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => addVoce(categoria)}>
        <Plus className="w-4 h-4 mr-1" /> Aggiungi voce
      </Button>
    </div>
  );

  const handleExport = () => {
    exportCsv(
      "listino_prima_nota.csv",
      ["Categoria", "Voce", "Descrizione", "Prezzo"],
      draft.map((v) => [v.categoria, v.nome, v.descrizione, v.prezzoUnitario]),
    );
  };

  return (
    <Card className="border-0 shadow-lg rounded-2xl bg-white">
      <CardHeader className="border-b bg-slate-50 rounded-t-2xl flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <Coins className="w-5 h-5 text-amber-600" />
            Listino Prezzi
          </CardTitle>
          <p className="text-sm text-slate-600 mt-1">
            Gestisci i prezzi di ausili e servizi utilizzati per la fatturazione agli ospiti.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button type="button" variant="outline" size="sm" onClick={handleExport}>
            <Download className="w-4 h-4 mr-1" /> Esporta Excel
          </Button>
          <Button type="button" size="sm" className="bg-rose-500 hover:bg-rose-600" onClick={() => addVoce("servizi")}>
            <Plus className="w-4 h-4 mr-1" /> Aggiungi voce
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-4 md:p-6">
        {renderTable(ausili, "ausili", "Ausili")}
        {renderTable(servizi, "servizi", "Spese fisse / servizi")}
        <Button type="button" className="bg-amber-600 hover:bg-amber-700" onClick={onSave} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
          Salva listino
        </Button>
        <p className="text-xs text-slate-500 mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
          I prezzi del listino vengono applicati automaticamente a consumi, spese extra, ricorrenze e report.
        </p>
      </CardContent>
    </Card>
  );
}
